/**
 * Deep freezes an object during development and testing so that we can prevent
 * bugs that are caused by object mutation. By using freeze() during
 * development, we can avoid having to use a more complex solution like
 * Immutable.js.
 *
 * @example
 * const myData = freeze<DataType>({data: [1, 2, 3]});
 * myData.data[0] = 42; // <-- throws an error
 *
 */
export default function freeze<T>(object: T): T {
  if (
    process.env.NODE_ENV === "development" ||
    process.env.NODE_ENV === "test"
  ) {
    // Retrieve the property names defined on object
    const propNames = Object.getOwnPropertyNames(object);

    // Set type as string-keyed object
    const obj = object as { [index: string]: any };

    // Freeze properties before freezing self
    for (let name of propNames) {
      const value = obj[name];

      obj[name] = value && typeof value === "object" ? freeze(value) : value;
    }
    return Object.freeze(obj as T);
  }
  return object;
}
